import type { QuoteMetadata } from "~types/types";
import { getDescriptionAndTitle } from "~utils";
import buildTickerKeywordsForQuoteType from "~utils/seo/keywords/buildTickerKeywordsForQuoteType";
import type { QuoteContextType } from "~context/QuoteContext";

const NOT_AVAILABLE = "--";
const BASE_QUOTE_URL = "https://www.bloomberg.com/quote";

export const getDisplayName = (
  type: string,
  shortName: string,
  longName: string
) => {
  let displayName;

  if (type === "CUR") {
    displayName = shortName || longName;
  } else {
    displayName = longName || shortName;
  }

  return displayName || NOT_AVAILABLE;
};

export const getCanonicalUrl = (id: string) => {
  return `${BASE_QUOTE_URL}/${id}`;
};

export const getQuoteStructuredData = (
  quoteData: QuoteContextType
): QuoteMetadata => {
  const {
    bicsSector,
    boardMembers,
    companyAddress,
    companyDescription,
    companyIsPrivate,
    companyPhone,
    companyWebsite,
    executives,
    foundedYear,
    id,
    mediaSecurityType,
    mediaSecuritySubtype,
    name,
    numberOfEmployees,
    pageType,
    primaryExchange,
    type,
    securityName,
    longName,
  } = quoteData;

  const [ticker] = id.split(":");
  const url = getCanonicalUrl(id);
  const displayName = getDisplayName(type, name, longName);

  const { title, description, constructedTitle } = getDescriptionAndTitle(
    type,
    displayName,
    id,
    primaryExchange,
    securityName,
    mediaSecuritySubtype
  );

  const keywords = buildTickerKeywordsForQuoteType({
    type,
    displayName,
    ticker,
    securityName,
  });

  const shortTickerId = id ? decodeURIComponent(ticker) : "";
  const shortExchange = primaryExchange ? primaryExchange.split(" ")[0] : "";

  return {
    bicsSector: bicsSector || undefined,
    company: {
      boardMembers: boardMembers || [],
      companyAddress: companyAddress || null,
      companyDescription: companyDescription || null,
      companyIsPrivate: Boolean(companyIsPrivate),
      companyPhone: companyPhone || null,
      executives: executives || [],
      companyWebsite: companyWebsite || null,
      foundedYear: foundedYear || null,
      numberOfEmployees: numberOfEmployees || null,
    },
    constructedTitle: constructedTitle || null,
    description,
    displayName,
    keywords,
    mediaSecurityType,
    pageType,
    primaryExchange,
    securityName,
    shortTickerId,
    shortExchange,
    tickerId: id,
    tickerName: name,
    title,
    type,
    url,
  };
};
